import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpEvent, HttpResponse } from "@angular/common/http";
import {catchError, from, Observable, of} from 'rxjs';
import { DescriptiveFormTemplate, FormTemplate } from "../forms/forms.model";
import { API_URL } from 'src/environments/environment';
import { ConfigAPI } from "../configuration/configuration.model";
import { map, take } from "rxjs/operators";
import {
  ChangeDirectoryCommand,
  FileNode,
  LibraryDirectory,
  LibraryDirectoryCreateCommand,
  LibraryDirectoryEditCommand,
  LibraryDocument,
  LibraryDocumentEditCommand,
  LibraryType,
} from "../library/library.model";
import { SequentialOrderAction } from "../sequential-order-actions/sequential-order-actions-model";
import { MatSnackBar, MatSnackBarConfig } from "@angular/material/snack-bar";
import { ref, getDownloadURL } from 'firebase/storage';
import { firebaseStorage } from 'src/app/firebase';

@Injectable({
  providedIn: "root",
})
export class DatabaseService {
  private http = inject(HttpClient);
  private snackBar = inject(MatSnackBar);
  private storage = firebaseStorage;

  loadForms(): Observable<FormTemplate[]> {
    return this.http.get<FormTemplate[]>(API_URL + "form");
  }

  loadDescriptiveForms(): Observable<DescriptiveFormTemplate[]> {
    return this.http.get<DescriptiveFormTemplate[]>(API_URL + "form/short");
  }
  findFormByUuid(formUuid: string): Observable<FormTemplate> {
    return this.http.get<FormTemplate>(API_URL + "form/" + formUuid);
  }

  addForm(form: FormTemplate): Observable<HttpResponse<FormTemplate>> {
    return this.http.post<FormTemplate>(API_URL + "form", form, {
      observe: "response",
    });
  }

  loadConfig(): Observable<ConfigAPI | null> {
    return this.http
      .get<ConfigAPI>(API_URL + "config")
      .pipe(map((response) => response[0]));
  }

  saveConfig(
    orgMap: Map<string, string>,
    uuid: string,
  ): Observable<HttpResponse<unknown>> {
    const convMap = {};
    orgMap.forEach((val: string, key: string) => {
      convMap[key] = val;
    });
    const payload = {
      uuid,
      externalId: null,
      configMap: convMap,
    };
    return this.http.post<Map<string, string>>(API_URL + "config", payload, {
      observe: "response",
    });
  }

  loadDocuments(): Observable<LibraryDocument[]> {
    return this.http.get<LibraryDocument[]>(API_URL + "librarydocument");
  }

  loadDocument(uuid: string): Observable<LibraryDocument> {
    return this.http.get<LibraryDocument>(API_URL + "librarydocument/" + uuid);
  }

  // Add this method to your DatabaseService class

  /**
   * Gets the content of a document as a Blob for viewing
   * @param documentUuid - The UUID of the document to retrieve
   * @returns Observable<Blob> - The document content as a blob
   */

  getFileById(path: string): Observable<string | null> {
    const storageRef = ref(this.storage, path);

    return from(getDownloadURL(storageRef)).pipe(
      catchError((error) => {
        console.error('Error getting file URL', error);
        return of(null);
      })
    );
  }

  getFileFromStorage(uri: string): Observable<Blob> {
    return this.http.get(uri, {
      responseType: "blob",
    });
  }

  loadDirectory(uuid: string): Observable<LibraryDirectory> {
    return this.http.get<LibraryDirectory>(API_URL + "directory/" + uuid);
  }

  addDocumentToLibrary(doc: FormData): Observable<HttpEvent<FormData>> {
    return this.http.post<FormData>(API_URL + "librarydocument/create", doc, {
      reportProgress: true,
      observe: "events",
    });
  }

  updateDocumentInLibrary(
    cmd: LibraryDocumentEditCommand,
  ): Observable<HttpResponse<unknown>> {
    return this.http.put<LibraryDocumentEditCommand>(
      API_URL + "librarydocument/update",
      cmd,
      {
        observe: "response",
      },
    );
  }

  deleteDocumentInLibrary(uuid: string): Observable<HttpResponse<unknown>> {
    return this.http.delete<string>(API_URL + "librarydocument/" + uuid, {
      observe: "response",
    });
  }

  loadFileHierarchy(type: LibraryType): Observable<FileNode[]> {
    return this.http.get<FileNode[]>(
      API_URL + "directory/hierarchy/type/" + type,
    );
  }

  addDirToLibrary(
    cmd: LibraryDirectoryCreateCommand,
  ): Observable<HttpResponse<unknown>> {
    return this.http.post<LibraryDirectoryCreateCommand>(
      API_URL + "directory/create",
      cmd,
      {
        observe: "response",
      },
    );
  }

  updateDirInLibrary(
    cmd: LibraryDirectoryEditCommand,
  ): Observable<HttpResponse<unknown>> {
    return this.http.put<LibraryDirectoryEditCommand>(
      API_URL + "directory/update",
      cmd,
      {
        observe: "response",
      },
    );
  }

  changeDirLocationInLibrary(
    cmd: ChangeDirectoryCommand,
  ): Observable<HttpResponse<unknown>> {
    return this.http.put<ChangeDirectoryCommand>(
      API_URL + "directory/changeLocation",
      cmd,
      {
        observe: "response",
      },
    );
  }

  changeFileLocationInLibrary(
    cmd: ChangeDirectoryCommand,
  ): Observable<HttpResponse<unknown>> {
    return this.http.put<ChangeDirectoryCommand>(
      API_URL + "librarydocument/changeLocation",
      cmd,
      {
        observe: "response",
      },
    );
  }

  readDirectories(type: LibraryType): Observable<LibraryDirectory[]> {
    return this.http.get<LibraryDirectory[]>(
      API_URL + "directory/type/" + type,
    );
  }

  deleteDirectoryInLibrary(uuid: string): Observable<HttpResponse<unknown>> {
    return this.http.delete<string>(API_URL + "directory/" + uuid, {
      observe: "response",
    });
  }

  loadSequenceActions(): Observable<SequentialOrderAction[]> {
    return this.http.get<SequentialOrderAction[]>(API_URL + "sequentialaction");
  }
  findSequentialActionByUuid(
    seqAction: string,
  ): Observable<SequentialOrderAction> {
    return this.http.get<SequentialOrderAction>(
      API_URL + "sequentialaction/" + seqAction,
    );
  }

  addSequentialAction(
    seqAct: SequentialOrderAction,
  ): Observable<HttpResponse<SequentialOrderAction>> {
    return this.http.post<SequentialOrderAction>(
      API_URL + "sequentialaction",
      seqAct,
      {
        observe: "response",
      },
    );
  }

  savingErrorMessage() {
    console.log("Save ERROR!");
    const config = new MatSnackBarConfig();
    config.panelClass = ["app-snack-panel"];
    config.duration = 2500;
    config.verticalPosition = "top";
    this.snackBar.open(
      "Data save error",
      "Problem with saving to database",
      config,
    );
  }
}
